import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { CalendarIcon, Archive, AlertCircle } from 'lucide-react'
import axios from 'axios'
import { format } from 'date-fns'
import { zhTW } from 'date-fns/locale'
import { useArchivedFiles } from '@/hooks/useAuditLogArchive'
import { Skeleton } from '@/components/ui/skeleton'
import { apiClient } from '@/libs/api_client'
import { useAuth } from '@/hooks/useAuth'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { logger } from '@magnetar/utils'

export function AuditLogArchive() {
  const { token } = useAuth()
  const { files, isLoading, isError, mutate } = useArchivedFiles()
  const [isArchiving, setIsArchiving] = useState(false)
  const [showArchiveDialog, setShowArchiveDialog] = useState(false)

  // Manual archive
  const [startDate, setStartDate] = useState<Date>()
  const [endDate, setEndDate] = useState<Date>()
  const [deleteAfterArchive, setDeleteAfterArchive] = useState(false)

  const handleManualArchive = async () => {
    if (!startDate || !endDate) {
      alert('請選擇開始和結束日期')
      return
    }

    setIsArchiving(true)
    try {
      const response = await apiClient.post(
        '/api/audit-logs/archive/manual',
        {
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
          delete_after_archive: deleteAfterArchive,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.data.success) {
        alert(
          `歸檔成功!\n已歸檔: ${response.data.data.archived} 筆\n已刪除: ${response.data.data.deleted} 筆`,
        )
        mutate()
        setStartDate(undefined)
        setEndDate(undefined)
        setShowArchiveDialog(false)
      }
    } catch (error) {
      logger.error('Manual archive error:', error)
      const message = axios.isAxiosError<{ error?: { message?: string } }>(error)
        ? error.response?.data?.error?.message || error.message
        : error instanceof Error
          ? error.message
          : String(error)
      alert('歸檔失敗: ' + message)
    } finally {
      setIsArchiving(false)
    }
  }

  const handleAutoArchive = async () => {
    setIsArchiving(true)
    try {
      const response = await apiClient.post(
        '/api/audit-logs/archive/auto',
        {
          delete_after_archive: true,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.data.success) {
        alert(
          `自動歸檔成功!\n已歸檔: ${response.data.data.archived} 筆\n已刪除: ${response.data.data.deleted} 筆`,
        )
        mutate()
      }
    } catch (error) {
      logger.error('Auto archive error:', error)
      const message = axios.isAxiosError<{ error?: { message?: string } }>(error)
        ? error.response?.data?.error?.message || error.message
        : error instanceof Error
          ? error.message
          : String(error)
      alert('自動歸檔失敗: ' + message)
    } finally {
      setIsArchiving(false)
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }

  if (isError) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-destructive">載入歸檔檔案列表失敗。請稍後再試。</div>
        </CardContent>
      </Card>
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-[200px]" />
        <Skeleton className="h-[300px]" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Archive Actions */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">手動歸檔</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              選擇特定日期範圍進行歸檔。歸檔後的資料將儲存至 Cloud Storage。
            </p>
            <Button onClick={() => setShowArchiveDialog(true)} disabled={isArchiving}>
              <Archive className="mr-2 h-4 w-4" />
              設定手動歸檔
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">自動歸檔 (3 個月前)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              自動歸檔 3 個月前的日誌。僅在 Production 環境執行,並會自動刪除已歸檔的資料。
            </p>
            <Button onClick={handleAutoArchive} disabled={isArchiving} variant="secondary">
              <Archive className="mr-2 h-4 w-4" />
              {isArchiving ? '執行中...' : '執行自動歸檔'}
            </Button>
          </CardContent>
        </Card>
      </div>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <strong>注意:</strong> 歸檔功能需要正確設定 Google Cloud Storage 環境變數。請參考文件:
          docs/audit-log-archive-setup.md
        </AlertDescription>
      </Alert>

      {/* Archived Files List */}
      <Card>
        <CardHeader>
          <CardTitle>已歸檔檔案列表</CardTitle>
        </CardHeader>
        <CardContent>
          {files && files.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium">檔案名稱</th>
                    <th className="text-left p-3 font-medium">檔案大小</th>
                    <th className="text-left p-3 font-medium">建立時間</th>
                    <th className="text-left p-3 font-medium">更新時間</th>
                  </tr>
                </thead>
                <tbody>
                  {files.map((file) => (
                    <tr key={file.name} className="border-t hover:bg-muted/30">
                      <td className="p-3 text-sm font-mono">{file.name}</td>
                      <td className="p-3 text-sm">{formatFileSize(file.size)}</td>
                      <td className="p-3 text-sm">
                        {format(new Date(file.created), 'yyyy-MM-dd HH:mm:ss')}
                      </td>
                      <td className="p-3 text-sm">
                        {format(new Date(file.updated), 'yyyy-MM-dd HH:mm:ss')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center text-muted-foreground py-8">尚無歸檔檔案</div>
          )}
        </CardContent>
      </Card>

      {/* Manual Archive Dialog */}
      <AlertDialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>手動歸檔設定</AlertDialogTitle>
            <AlertDialogDescription>
              選擇要歸檔的日期範圍。歸檔後的資料將儲存至 Cloud Storage。
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">開始日期</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, 'PPP', { locale: zhTW }) : '選擇日期'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={startDate} onSelect={setStartDate} />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">結束日期</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, 'PPP', { locale: zhTW }) : '選擇日期'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={endDate} onSelect={setEndDate} />
                </PopoverContent>
              </Popover>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="deleteAfterArchive"
                checked={deleteAfterArchive}
                onChange={(e) => setDeleteAfterArchive(e.target.checked)}
                className="rounded border-gray-300"
              />
              <label htmlFor="deleteAfterArchive" className="text-sm font-medium cursor-pointer">
                歸檔後刪除資料庫中的日誌
              </label>
            </div>

            {deleteAfterArchive && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  <strong>警告:</strong> 此操作將永久刪除資料庫中的日誌。請確保歸檔成功後再刪除。
                </AlertDescription>
              </Alert>
            )}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={handleManualArchive} disabled={isArchiving || !startDate || !endDate}>
              {isArchiving ? '執行中...' : '開始歸檔'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
